import { useEffect, useState, type Dispatch, type SetStateAction } from "react";
import * as orderService from "@/supabase/order/service/orderService";
import { downloadExcel, downloadExcelWithTemplate, type CustomerRow } from "@/utils/excelUtils";
import type { ParsingOptions } from "@/hooks/useParsingOptions";
import type { DeliveryTemplate } from "@/utils/templateParser";
import { logUsage } from "@/lib/usageLog";
import { logError } from "@/lib/sentry";
import { toast } from "sonner";

const EMPTY_KEEP_SET: ReadonlySet<string> = new Set<string>();

/**
 * rows에서 킵 닉네임과 정확 일치하는 행을 제외한다.
 * 빈 닉네임 행은 매치 불가 → 항상 포함(잘못 빼는 것보다 안전한 방향).
 */
export function excludeKeepRows(
  rows: CustomerRow[],
  keepNicknames: ReadonlySet<string>
): { filteredRows: CustomerRow[]; excludedRowCount: number; excludedNicknameCount: number } {
  const excludedNicknames = new Set<string>();
  const filteredRows = rows.filter(row => {
    if (row.nickname && keepNicknames.has(row.nickname)) {
      excludedNicknames.add(row.nickname);
      return false;
    }
    return true;
  });
  return {
    filteredRows,
    excludedRowCount: rows.length - filteredRows.length,
    excludedNicknameCount: excludedNicknames.size,
  };
}

interface UseExcelKeepInfoProps {
  userId: string | null;
  /** yyyy-MM-dd */
  orderDate: string;
  /** 회원 여부. false면 조회하지 않고 빈 Set 유지. */
  enabled: boolean;
  /** NewExcel의 rows fetch 로딩 상태 — 킵 Set과 rows의 날짜 스냅샷 정합 가드용. */
  rowsLoading: boolean;
  /** NewExcel의 화면 rows — live 참조 주입(snapshot 금지). */
  rows: CustomerRow[];
  parsingOptions: ParsingOptions;
  getPseudoUserId: () => string;
  setError: Dispatch<SetStateAction<string | null>>;
  openLoginModal: () => void;
}

interface UseExcelKeepInfoResult {
  keepNicknames: ReadonlySet<string>;
  isKeepInfoLoading: boolean;
  isKeepInfoError: boolean;
  handleExcludeKeepExcelDownload: (template?: DeliveryTemplate | null) => Promise<void>;
}

/**
 * 배송엑셀 킵 고객 정보 도메인 훅.
 * (userId, orderDate) 변경 시 킵 닉네임 Set을 조회하고(배지·다운로드 공용 스냅샷 = WYSIWYG),
 * '킵 제외 (선택된 날짜)' 모드의 다운로드 핸들러를 제공한다.
 * 다운로드 직전 재조회는 오너 판단으로 기각(오버엔지니어링, 2026-07-10) — 로딩/실패 가드만 둔다.
 */
export function useExcelKeepInfo({
  userId,
  orderDate,
  enabled,
  rowsLoading,
  rows,
  parsingOptions,
  getPseudoUserId,
  setError,
  openLoginModal,
}: UseExcelKeepInfoProps): UseExcelKeepInfoResult {
  const [keepNicknames, setKeepNicknames] = useState<ReadonlySet<string>>(EMPTY_KEEP_SET);
  const [isKeepInfoLoading, setIsKeepInfoLoading] = useState(false);
  const [isKeepInfoError, setIsKeepInfoError] = useState(false);

  useEffect(() => {
    if (!enabled || !userId) {
      setKeepNicknames(EMPTY_KEEP_SET);
      setIsKeepInfoLoading(false);
      setIsKeepInfoError(false);
      return;
    }
    let cancelled = false;
    let requestSeq = 0;
    const fetchKeepNicknames = () => {
      const seq = ++requestSeq;
      setIsKeepInfoLoading(true);
      setIsKeepInfoError(false);
      orderService
        .getKeepNicknamesByDate(userId, orderDate)
        .then(nicknames => {
          if (cancelled || seq !== requestSeq) return;
          setKeepNicknames(new Set(nicknames));
        })
        .catch(err => {
          if (cancelled || seq !== requestSeq) return;
          logError("킵 닉네임 조회 실패:", err);
          setKeepNicknames(EMPTY_KEEP_SET);
          setIsKeepInfoError(true);
        })
        .finally(() => {
          if (cancelled || seq !== requestSeq) return;
          setIsKeepInfoLoading(false);
        });
    };
    fetchKeepNicknames();
    // 탭 복귀 시 재조회: NewExcel의 rows visibilitychange 재조회와 트리거 대칭 (PR #109 봇 P2 오너 수용, 2026-07-11).
    // seq 가드: 재조회 응답이 뒤바뀌어 도착해도 최신 요청만 반영.
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        fetchKeepNicknames();
      }
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      cancelled = true;
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [userId, orderDate, enabled]);

  const handleExcludeKeepExcelDownload = async (template?: DeliveryTemplate | null) => {
    if (!enabled || !userId) {
      toast.info("로그인 후 이용 가능한 기능입니다.");
      openLoginModal();
      return;
    }
    if (rowsLoading || isKeepInfoLoading) {
      toast.info("킵 고객 정보를 불러오는 중이에요. 잠시 후 다시 시도해주세요.");
      return;
    }
    if (isKeepInfoError) {
      toast.error("킵 고객 정보를 불러오지 못했어요. 새로고침 후 다시 시도해주세요.");
      return;
    }
    if (rows.length === 0) {
      setError("고객 정보를 추가해보세요!");
      window.alert("아직 추가된 고객 정보가 없습니다. 고객 정보를 추가한 후 다시 시도해주세요.");
      return;
    }

    const { filteredRows, excludedRowCount, excludedNicknameCount } = excludeKeepRows(rows, keepNicknames);
    if (filteredRows.length === 0) {
      toast.info("모든 행이 킵 고객이라 다운로드할 데이터가 없어요.");
      return;
    }

    try {
      if (template) {
        await downloadExcelWithTemplate(filteredRows, template);
      } else {
        await downloadExcel(filteredRows, parsingOptions);
      }

      await logUsage({
        userId: userId || null,
        pseudoUserId: getPseudoUserId(),
        isMember: !!userId,
        type: template ? "excel_template" : "excel",
      }).catch(err => {
        logError("사용 로그 기록 중 오류 발생:", err);
      });

      if (excludedRowCount > 0) {
        toast.success(`킵 고객 ${excludedNicknameCount}명(${excludedRowCount}행)을 제외하고 다운로드했어요.`);
      }
    } catch (error) {
      logError("Exclude-keep Excel download error:", error);
      toast.error("엑셀 파일 다운로드 중 오류가 발생했습니다.");
    }
  };

  return { keepNicknames, isKeepInfoLoading, isKeepInfoError, handleExcludeKeepExcelDownload };
}
